// HomeSignalsPreview — top-4 signal rows matching the prototype's SignalsPreview.
//
// Bound to useFairPriceCards({ limit: 4 }) and usePublicSignalCardWatchlist
// for star toggle state. Curated public-read boundary — no fabricated
// fixtures, no live odds.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";
import {
  useFairPriceCards,
  type FairPriceCardViewModel,
} from "../../exchange/useFairPriceCards.ts";
import { usePublicSignalCardWatchlist } from "../../exchange/usePublicSignalCardWatchlist.ts";

interface SignalVM {
  id: string;
  home: string;
  away: string;
  league: string;
  kick: string;
  prob: number; // 0..100
  edgeText: string;
  edgeClass: "pos" | "neg" | "neu";
  conf: number; // 0..4
  marketOdds: string;
}

function fmtKickoff(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function confLevel(label: string | null): number {
  if (label === "high") return 4;
  if (label === "medium") return 3;
  if (label === "low") return 2;
  return 1;
}

function fmtEdge(value_edge: number | null): { text: string; cls: "pos" | "neg" | "neu" } {
  if (value_edge === null || !Number.isFinite(value_edge)) return { text: "—", cls: "neu" };
  const sign = value_edge > 0 ? "+" : value_edge < 0 ? "−" : "";
  const abs = Math.abs(value_edge).toFixed(1);
  const cls: "pos" | "neg" | "neu" = value_edge > 0 ? "pos" : value_edge < 0 ? "neg" : "neu";
  return { text: `${sign}${abs}%`, cls };
}

function toVM(card: FairPriceCardViewModel): SignalVM {
  const fix = card.fixture_label;
  const home = fix?.home_team ?? "Home";
  const away = fix?.away_team ?? "Away";
  const league = fix?.league_name ?? "—";
  const kick = fmtKickoff(fix?.kickoff_at ?? null);
  const probPct =
    card.fair_probability !== null && Number.isFinite(card.fair_probability)
      ? Math.round(card.fair_probability * 100)
      : 0;
  const { text: edgeText, cls: edgeClass } = fmtEdge(card.value_edge);
  const conf = confLevel(card.confidence_label);
  const marketOdds =
    card.market_odds_decimal !== null && Number.isFinite(card.market_odds_decimal)
      ? card.market_odds_decimal.toFixed(2)
      : "—";
  return {
    id: card.id,
    home,
    away,
    league,
    kick,
    prob: probPct,
    edgeText,
    edgeClass,
    conf,
    marketOdds,
  };
}

export function HomeSignalsPreview(): JSX.Element {
  const { cards, loading } = useFairPriceCards({ limit: 4 });
  const watchlist = usePublicSignalCardWatchlist();

  const vms = useMemo(() => cards.map(toVM), [cards]);

  return (
    <div className="home-card">
      <div className="card-head">
        <div className="h-title">Signals from the Exchange</div>
        <div className="h-sub">· top 4 by edge</div>
        <div className="h-actions">
          <Link className="link" to="/exchange">
            Open Exchange <Icon name="chev" size={12} />
          </Link>
        </div>
      </div>
      <div className="card-body sig-list">
        {loading && vms.length === 0 ? (
          <div className="sig-empty">Loading signals…</div>
        ) : vms.length === 0 ? (
          <div className="sig-empty">
            No signals ready yet. The Exchange surfaces public-ready cards as the daily
            intelligence pipeline produces them.
          </div>
        ) : (
          vms.map((s) => {
            const watched = watchlist.isSaved(s.id);
            return (
              <div className="sig-row" key={s.id}>
                <div className="sig-match">
                  <div className="teams">
                    {s.home} <span className="vs">vs</span> {s.away}
                  </div>
                  <div className="meta">
                    <span>{s.league}</span>
                    <span className="dot" />
                    <span>{s.kick}</span>
                    <span className="dot" />
                    <span>@ {s.marketOdds}</span>
                  </div>
                </div>
                <div className="sig-prob">
                  <div className="prob-bar">
                    <div className="fill" style={{ width: `${String(s.prob)}%` }} />
                  </div>
                  <div className="pct">{String(s.prob)}%</div>
                </div>
                <div className={`sig-edge ${s.edgeClass}`}>{s.edgeText}</div>
                <div className="sig-conf" title={`Confidence ${String(s.conf)}/4`}>
                  {[1, 2, 3, 4].map((p) => (
                    <span key={p} className={`pip ${p <= s.conf ? "on" : ""}`} />
                  ))}
                </div>
                <button
                  type="button"
                  className={`sig-watch ${watched ? "on" : ""}`}
                  title={watched ? "Watching" : "Add to watchlist"}
                  onClick={() => {
                    void watchlist.toggleSignalCard(s.id);
                  }}
                >
                  <Icon name="star" size={13} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
